import { NavLink } from "react-router-dom";
import { PlanLoadingDialogs } from "@opstel/dialogs/PlanLoadingDialogs";
import { useTheme } from "@shared/EDS/contexts/useTheme";
import {
  useHandleDownloadInstance,
  useHandleOpenInstance,
} from "@shared/common/hooks/useInstanceOperations";
import { useHasInstance } from "@shared/common/contexts/instanceMetadata/useHasInstance";
import { useCallback } from "react";
import { messageToast } from "@shared/EDS/components/MessageToast/messageToastFunctions";
import { LoadSpecificInstanceDialog } from "@opstel/dialogs/LoadSpecificInstance/LoadSpecificInstanceDialog";
import { AboutDialog } from "@shared/dialogs/AboutDialog";
import { useMsalAccount } from "@shared/auth/hooks/useMsalAccount";
import logo from "../../assets/KnopLogo-mask.svg";
import { config } from "@shared/envVariables";
import { DisruptionMessageBanner } from "../DisruptionMessageBanner.tsx";
import { POPUP_MENU_DISTANCE } from "@shared/common/layoutConstants";
import { ReleaseNotesDialog } from "@shared/dialogs/ReleaseNotesDialog";
import { hidePopoverById } from "@shared/common/utils/popoverHelpers";
import { useToggleableDialog } from "@shared/common/hooks/useToggleableDialog";
import { FunctionalMessageBanner } from "@shared/components/FunctionalMessageBanner.tsx";
import { appRoutes } from "@shared/routes/routes";

export function NavigationBar() {
  return (
    <>
      <tractie-navigation-bar data-testid="nav-root">
        <tractie-navigation-bar-top>
          <tractie-logo slot="start" logo-name="KnoP" logo-image={logo} />
          <tractie-navigation-bar-menu>
            <tractie-menuitem>
              <NavLink to={appRoutes.SBD} data-testid="nav-link-sbd">
                SBD
              </NavLink>
            </tractie-menuitem>
            <tractie-menuitem>
              <NavLink to={appRoutes.EPD} data-testid="nav-link-epd">
                EPD
              </NavLink>
            </tractie-menuitem>
            <tractie-menuitem>
              <button type="button" data-testid="nav-link-hip">
                HIP
              </button>
              <tractie-sub-menu>
                <tractie-menuitem>
                  <NavLink to={appRoutes.RET_SHIFTS_OVERVIEW} data-testid="nav-link-hip-personeel">
                    Personeel
                  </NavLink>
                </tractie-menuitem>
                <tractie-menuitem>
                  <NavLink to={appRoutes.SERVICETAKEN} data-testid="nav-link-servicetaken">
                    Servicetaken oplossing
                  </NavLink>
                </tractie-menuitem>
              </tractie-sub-menu>
            </tractie-menuitem>
            {config.personeel.enabled && (
              <>
                <tractie-menuitem>
                  <NavLink to={appRoutes.PERSONEEL} data-testid="nav-link-personeel">
                    Personeel
                  </NavLink>
                </tractie-menuitem>
                <tractie-menuitem>
                  <NavLink to={appRoutes.PERSONEEL_WERKLIJNEN} data-testid="nav-link-werklijnen">
                    Werklijnen
                  </NavLink>
                </tractie-menuitem>
              </>
            )}
          </tractie-navigation-bar-menu>
          <tractie-navigation-tertiary slot="end">
            <PlanLoadingDialogs />
            <InfoSubMenu />
            <SettingsSubMenu />
            <UserSubMenu />
          </tractie-navigation-tertiary>
        </tractie-navigation-bar-top>
      </tractie-navigation-bar>
      <DisruptionMessageBanner />
      <FunctionalMessageBanner />
    </>
  );
}

function InfoSubMenu() {
  const releaseNotesDialog = useToggleableDialog();
  const aboutDialog = useToggleableDialog();

  return (
    <tractie-container data-testid="nav-info-container">
      <tractie-button level="tertiary" icon-only id="info" data-testid="nav-info-button">
        <tractie-icon name="info" />
      </tractie-button>
      <tractie-popover
        id="info-popover"
        invoker-id="info"
        data-testid="popover-nav-info"
        auto-close={true}
        style={{ "--tractie-popover-inset-block-start": POPUP_MENU_DISTANCE + "px" }}
      >
        <tractie-list-item
          data-testid="action-release-notes"
          onClick={releaseNotesDialog.handleOpenDialog}
        >
          Release notes
        </tractie-list-item>
        <tractie-list-item data-testid="action-about-knop" onClick={aboutDialog.handleOpenDialog}>
          Over KnoP
        </tractie-list-item>
      </tractie-popover>
      <ReleaseNotesDialog
        dialogRef={releaseNotesDialog.dialogRef}
        hasBeenOpened={releaseNotesDialog.isOpen}
      />
      <AboutDialog dialogRef={aboutDialog.dialogRef} />
    </tractie-container>
  );
}

function SettingsSubMenu() {
  const { isLightTheme, setThemeByName } = useTheme();
  const onOpenInstance = useHandleOpenInstance();
  const onDownloadInstance = useHandleDownloadInstance();
  const instanceIsLoaded = useHasInstance();

  const handleToggleTheme = useCallback(() => {
    if (setThemeByName) {
      setThemeByName(isLightTheme ? "dark" : "light");
    }
  }, [isLightTheme, setThemeByName]);

  const loadSpecificInstanceDialog = useToggleableDialog();

  return (
    <tractie-container data-testid="nav-settings-container">
      <tractie-button level="tertiary" icon-only id="settings" data-testid="nav-settings-button">
        <tractie-icon name="settings" />
      </tractie-button>
      <tractie-popover
        id="settings-popover"
        invoker-id="settings"
        data-testid="popover-nav-settings"
        style={{ "--tractie-popover-inset-block-start": POPUP_MENU_DISTANCE + "px" }}
      >
        <tractie-list-item
          data-testid="action-theme-toggle"
          icon-leading="dark-light-mode"
          label={isLightTheme ? "Donkere modus" : "Lichte modus"}
          onClick={() => {
            handleToggleTheme();
            hidePopoverById("settings-popover");
          }}
        />
        <tractie-list-item
          data-testid="action-open-kpp"
          icon-leading="folder"
          label="Openen kpp"
          onClick={() => {
            onOpenInstance.mutate();
            hidePopoverById("settings-popover");
          }}
        />
        <tractie-list-item
          data-testid="action-save-kpp"
          icon-leading="save"
          label="Opslaan kpp"
          onClick={() => {
            if (!instanceIsLoaded) {
              console.warn("No instance is currently loaded. Please open an instance first.");
              messageToast({
                message: `Opslaan is niet mogelijk omdat er geen plan is geladen.`,
                type: "error",
              });
              return;
            }
            onDownloadInstance.mutate();
            hidePopoverById("settings-popover");
          }}
        />
        <tractie-list-item
          data-testid="action-load-plan-id"
          icon-leading="download"
          onClick={loadSpecificInstanceDialog.handleOpenDialog}
        >
          Plan laden met ID
        </tractie-list-item>
      </tractie-popover>
      <LoadSpecificInstanceDialog dialogRef={loadSpecificInstanceDialog.dialogRef} />
    </tractie-container>
  );
}

function UserSubMenu() {
  const { instance, account } = useMsalAccount();

  const handleLogoutRedirect = useCallback(() => {
    void instance.logoutRedirect({ account });
  }, [account, instance]);

  return (
    <tractie-container data-testid="nav-user-container">
      <tractie-button level="tertiary" icon-only id="user" data-testid="nav-user-button">
        <tractie-icon name="user" />
      </tractie-button>
      <tractie-popover
        id="user-popover"
        invoker-id="user"
        data-testid="popover-nav-user"
        style={{ "--tractie-popover-inset-block-start": POPUP_MENU_DISTANCE + "px" }}
      >
        <tractie-list-item
          data-testid="action-logout"
          icon-leading="log-out"
          label="Uitloggen"
          onClick={() => {
            handleLogoutRedirect();
            hidePopoverById("user-popover");
          }}
        />
      </tractie-popover>
    </tractie-container>
  );
}
